import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import useHeaderScroll from "@/lib/useHeaderScroll";
import { RE_BASE } from "@/lib/routes";

export default function RealEstateLanding() {
  const { t } = useLang();
  const r = t.re;
  const { hidden } = useHeaderScroll();

  return (
    <main className="grain bg-stone text-charcoal overflow-x-hidden relative" data-testid="re-landing">
      <div className={`md:hidden fixed top-0 inset-x-0 z-30 flex items-center justify-between pl-5 pr-4 py-4 transition-transform duration-300 ${hidden ? "-translate-y-full" : "translate-y-0"}`}>
        <span data-testid="re-wordmark-mobile" className="font-heading font-semibold uppercase tracking-[0.16em] text-charcoal text-[1.15rem]" style={{ wordSpacing: "0.09em" }}>
          Eagle Eyes
        </span>
        <LanguageSwitcher variant="re" />
      </div>
      <div className="hidden md:block fixed top-4 right-4 z-50">
        <LanguageSwitcher variant="re" />
      </div>

      <section className="relative flex flex-col md:flex-row min-h-screen w-full">
        {/* Two independent half-spans pinned to the exact 50% line:
            left word's visual edge at -0.30em, right word's at +0.30em,
            so the split passes through the exact middle of the word gap. */}
        <div className="pointer-events-none absolute inset-0 top-4 z-40 hidden md:block mix-blend-difference">
          <span className="absolute right-1/2 top-0 mr-[0.14em] font-heading font-semibold uppercase tracking-[0.16em] text-paper text-[1.01rem] leading-none">Eagle</span>
          <span className="absolute left-1/2 top-0 ml-[0.3em] font-heading font-semibold uppercase tracking-[0.16em] text-paper text-[1.01rem] leading-none">Eyes</span>
        </div>

        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="contents"
        >
          <div
            className="group/half relative flex-1 min-h-[62vh] md:min-h-screen flex items-center justify-center overflow-hidden bg-stone text-charcoal"
            data-testid="re-half-property"
          >
            <div className="absolute inset-0">
              <img src="/assets/re-interior.webp" alt="Sunlit apartment interior" className="w-full h-full object-cover opacity-30 transition-all duration-700 group-has-[a:hover]/half:scale-105 group-has-[a:hover]/half:opacity-50" />
              <div className="absolute inset-0 bg-gradient-to-br from-stone via-stone/80 to-transparent" />
            </div>
            <div className="relative z-10 max-w-md px-6 py-14 md:py-10 text-center">
              <span className="font-mono-data text-[0.71rem] text-charcoal/55 block mb-4 md:mb-6">{r.forClients}</span>
              <h1 className="font-heading font-semibold italic text-[2rem] md:text-[3.57rem] leading-[1.04] mb-4 md:mb-5">{r.propertyH}</h1>
              <p className="text-[1rem] md:text-[1.15rem] leading-[1.6] text-charcoal/80 mb-6 md:mb-8 max-w-[27.6rem] mx-auto">{r.propertyP}</p>
              <Link
                to={`${RE_BASE}/property`}
                data-testid="re-enter-property"
                className="group inline-flex items-center gap-2 font-mono-data text-[0.76rem] border border-charcoal px-6 py-3.5 transition-all duration-300 hover:bg-charcoal hover:text-stone hover:-translate-y-0.5"
              >
                {r.propertyCta}
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="contents"
        >
          <div
            className="group/half relative flex-1 min-h-[62vh] md:min-h-screen flex items-center justify-center overflow-hidden bg-ink text-paper"
            data-testid="re-half-investment"
          >
            <div className="absolute inset-0">
              <img src="/assets/re-exterior.webp" alt="Architectural property exterior" className="w-full h-full object-cover opacity-30 transition-all duration-700 group-has-[a:hover]/half:scale-105 group-has-[a:hover]/half:opacity-50" />
              <div className="absolute inset-0 bg-gradient-to-bl from-ink via-ink/85 to-transparent" />
            </div>
            <div className="relative z-10 max-w-md px-6 py-14 md:py-10 text-center">
              <span className="font-mono-data text-[0.71rem] text-paper/55 block mb-4 md:mb-6">{r.forInvestors}</span>
              <h1 className="font-heading font-semibold text-[2rem] md:text-[3.57rem] leading-[1.04] mb-4 md:mb-5">{r.investmentH}</h1>
              <p className="text-[1rem] md:text-[1.15rem] leading-[1.6] text-paper/80 mb-6 md:mb-8 max-w-[27.6rem] mx-auto">{r.investmentP}</p>
              <Link
                to={`${RE_BASE}/investment`}
                data-testid="re-enter-investment"
                className="group inline-flex items-center gap-2 font-mono-data text-[0.76rem] border border-paper px-6 py-3.5 transition-all duration-300 hover:bg-paper hover:text-ink hover:-translate-y-0.5"
              >
                {r.investmentCta}
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </motion.div>

        <div className="pointer-events-none absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-brass/70 md:top-0 md:bottom-0 md:left-1/2 md:right-auto md:h-auto md:w-px md:translate-x-0 md:translate-y-0" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <div data-testid="re-medallion-home" title={t.nav.backToGroup} className="block w-[116px] h-[116px]">
              <img
                src="/assets/medallion-blend.png"
                alt="Eagle Eyes medallion"
                className="w-full h-full"
              />
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
